import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import ErrorBoundary from './components/ErrorBoundary';
import Layout from './components/Layout';
import SettingsShell from './components/settings/SettingsShell';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import DashboardPage from './pages/DashboardPage';
import NewStarterPage from './pages/NewStarterPage';
import LeaverPage from './pages/LeaverPage';
import EmployeeProfilePage from './pages/EmployeeProfilePage';
import SubmissionHistoryPage from './pages/SubmissionHistoryPage';
import AdminDashboard from './pages/AdminDashboard';
import ClientsPage from './pages/ClientsPage';
import TeamPage from './pages/TeamPage';
import AcceptInvitePage from './pages/AcceptInvitePage';
import StarterFormPage from './pages/StarterFormPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import SecurityPage from './pages/SecurityPage';
import EmailTemplatesPage from './pages/EmailTemplatesPage';
import EmailSchedulePage from './pages/EmailSchedulePage';
import ProcessingViewPage from './pages/ProcessingViewPage';
import PartTimePayPage from './pages/PartTimePayPage';
import ActivityPage from './pages/ActivityPage';
import AuditLogPage from './pages/AuditLogPage';
import IntegrationsPage from './pages/IntegrationsPage';
import DocumentsPage from './pages/DocumentsPage';
import NotFoundPage from './pages/NotFoundPage';

function AuthenticatedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center h-screen">Loading...</div>;
  return user ? children : <Navigate to="/login" />;
}

function PrivateRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center h-screen">Loading...</div>;
  if (!user) return <Navigate to="/login" />;
  if (user.requires_2fa_setup) return <Navigate to="/security" />;
  return children;
}

function AdminRoute({ children }) {
  const { user, loading, viewingAs, exitViewAsClient } = useAuth();
  useEffect(() => {
    if (viewingAs && !loading) exitViewAsClient({ redirect: false });
  }, [viewingAs, loading, exitViewAsClient]);
  if (loading) return <div className="flex items-center justify-center h-screen">Loading...</div>;
  if (viewingAs) return <div className="flex items-center justify-center h-screen">Returning to admin view...</div>;
  if (!user) return <Navigate to="/login" />;
  if (user.role !== 'admin') return <Navigate to="/" />;
  if (user.requires_2fa_setup) return <Navigate to="/security" />;
  return children;
}

function AdminTeamRoute({ children }) {
  const { user, loading, viewingAs, exitViewAsClient } = useAuth();
  useEffect(() => {
    if (viewingAs && !loading) exitViewAsClient({ redirect: false });
  }, [viewingAs, loading, exitViewAsClient]);
  if (loading) return <div className="flex items-center justify-center h-screen">Loading...</div>;
  if (viewingAs) return <div className="flex items-center justify-center h-screen">Returning to admin view...</div>;
  if (!user) return <Navigate to="/login" />;
  if (user.role !== 'admin' && user.role !== 'standard') return <Navigate to="/" />;
  if (user.requires_2fa_setup) return <Navigate to="/security" />;
  return children;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center h-screen">Loading...</div>;
  return user ? <Navigate to={(user.role === 'admin' || user.role === 'standard') ? '/admin' : '/'} /> : children;
}

// The standalone Run Sheet page was merged into ProcessingViewPage. Redirect any
// lingering bookmarks to the unified processing view for the same submission.
function RunSheetRedirect() {
  const { id } = useParams();
  return <Navigate to={`/admin/process/${id}`} replace />;
}

// Legacy /admin/integrations URL — preserve the query string so the Google
// Drive OAuth callback (?drive=connected) still surfaces its toast after the
// page moved into the Settings shell.
function IntegrationsRedirect() {
  const { search } = useLocation();
  return <Navigate to={`/settings/integrations${search}`} replace />;
}

function App() {
  return (
    <ErrorBoundary>
    <ThemeProvider>
    <AuthProvider>
      <Router>
        <Routes>
          <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
          <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
          <Route path="/accept-invite" element={<AcceptInvitePage />} />
          {/* The new starter has no account — the link token is the credential */}
          <Route path="/starter-form" element={<StarterFormPage />} />
          <Route path="/forgot-password" element={<PublicRoute><ForgotPasswordPage /></PublicRoute>} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/" element={<PrivateRoute><Layout fullWidth><DashboardPage /></Layout></PrivateRoute>} />
          <Route path="/new-starter" element={<PrivateRoute><Layout fullWidth><NewStarterPage /></Layout></PrivateRoute>} />
          <Route path="/leaver" element={<PrivateRoute><Layout fullWidth><LeaverPage /></Layout></PrivateRoute>} />
          <Route path="/documents" element={<PrivateRoute><Layout fullWidth><DocumentsPage /></Layout></PrivateRoute>} />
          <Route path="/employee/:id" element={<PrivateRoute><Layout fullWidth><EmployeeProfilePage /></Layout></PrivateRoute>} />
          <Route path="/history" element={<PrivateRoute><Layout fullWidth><SubmissionHistoryPage /></Layout></PrivateRoute>} />
          <Route path="/activity" element={<PrivateRoute><Layout><ActivityPage /></Layout></PrivateRoute>} />
          <Route path="/admin" element={<AdminTeamRoute><Layout fullWidth><AdminDashboard /></Layout></AdminTeamRoute>} />
          <Route path="/admin/clients" element={<AdminRoute><Layout fullWidth><ClientsPage /></Layout></AdminRoute>} />
          {/* Settings shell — consolidated admin sub-pages (Phase 4) */}
          <Route path="/settings" element={<Navigate to="/settings/team" replace />} />
          <Route path="/settings/team" element={<AdminRoute><Layout fullWidth><SettingsShell active="team"><TeamPage /></SettingsShell></Layout></AdminRoute>} />
          <Route path="/settings/integrations" element={<AdminRoute><Layout fullWidth><SettingsShell active="integrations"><IntegrationsPage /></SettingsShell></Layout></AdminRoute>} />
          <Route path="/settings/email-schedule" element={<AdminRoute><Layout fullWidth><SettingsShell active="email-schedule"><EmailSchedulePage /></SettingsShell></Layout></AdminRoute>} />
          <Route path="/settings/email-templates" element={<AdminRoute><Layout fullWidth><SettingsShell active="email-templates"><EmailTemplatesPage /></SettingsShell></Layout></AdminRoute>} />
          <Route path="/settings/part-time-pay" element={<AdminRoute><Layout fullWidth><SettingsShell active="part-time-pay" title="Part-Time Pay" subtitle="Check what part-time staff are actually paid, and record the working pattern their pay derives from."><PartTimePayPage /></SettingsShell></Layout></AdminRoute>} />
          <Route path="/settings/activity" element={<AdminRoute><Layout fullWidth><SettingsShell active="activity"><ActivityPage /></SettingsShell></Layout></AdminRoute>} />
          <Route path="/settings/audit" element={<AdminRoute><Layout fullWidth><SettingsShell active="audit"><AuditLogPage /></SettingsShell></Layout></AdminRoute>} />
          <Route path="/settings/security" element={<AdminRoute><Layout fullWidth><SettingsShell active="security"><SecurityPage /></SettingsShell></Layout></AdminRoute>} />
          {/* Legacy admin settings URLs → redirect into the Settings shell */}
          <Route path="/admin/team" element={<Navigate to="/settings/team" replace />} />
          <Route path="/admin/integrations" element={<IntegrationsRedirect />} />
          <Route path="/admin/email-schedule" element={<Navigate to="/settings/email-schedule" replace />} />
          <Route path="/admin/email-templates" element={<Navigate to="/settings/email-templates" replace />} />
          <Route path="/admin/audit-log" element={<Navigate to="/settings/audit" replace />} />
          <Route path="/admin/process/:id" element={<AdminTeamRoute><Layout fullWidth><ProcessingViewPage /></Layout></AdminTeamRoute>} />
          {/* Old run-sheet URL kept as a redirect so any bookmarks land on the new unified processing view */}
          <Route path="/admin/run-sheet/:id" element={<AdminTeamRoute><RunSheetRedirect /></AdminTeamRoute>} />
          <Route path="/security" element={<AuthenticatedRoute><Layout><SecurityPage /></Layout></AuthenticatedRoute>} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Router>
    </AuthProvider>
    </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
